import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import {
  AgencyLogo,
  Description,
  OffersCount,
  PhoneButtonContainer,
  PhonePlaceholder,
  Row,
  StyledAgencyCard,
  Title
} from './styled';
import { AgencyListItem } from '../../BusinessItemsListPage/types';
import { useFullPhoneNumbers } from '../../../hooks/useFullPhoneNumbers';
import { InteractionTargetTypeEnum } from '../../../graphql/types';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';
import { useImageRetryOnReconnect } from '../../../hooks/useImageRetryOnReconnect';
import { withCacheBuster } from '../../ui/ItemCard/helpers';

const BottomPhoneButton = dynamic(
  () => import('../../Ad/BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  { ssr: false }
);

export const AgencyCard: React.FC<AgencyListItem> = React.memo(
  ({ id, path, logoUrl, name, itemsCount = 0, description }) => {
    const { t } = useTranslation();
    const { isSmall } = useScreenSize();
    const isClient = useClient();
    const { imgElementRef, retryCacheBuster } = useImageRetryOnReconnect();

    const offersCount = useMemo(
      () => t('sections.agencies.agencies_count_many', { count: itemsCount }),
      [t, itemsCount]
    );

    const agencyPathId = useMemo(
      () => path?.replace('/agentlikler/', '') ?? '',
      [path]
    );

    const { onClickHandler, phonesShown, setIsVisible, isVisible } =
      useFullPhoneNumbers({
        id: agencyPathId,
        residenceId: null,
        path: 'agentlikler',
        targetType: InteractionTargetTypeEnum.Agency,
        agencyId: id
      });

    return (
      <StyledAgencyCard
        href={path}
        data-cy="agency"
        data-stat="agency-card-click"
      >
        <Row>
          <AgencyLogo
            ref={imgElementRef}
            src={
              withCacheBuster(logoUrl, retryCacheBuster) ??
              '/static/images/empty-agency-logo.svg'
            }
            alt={`${name} Logo`}
            data-cy="agency-logo"
            width={55}
            height={55}
            loading="lazy"
          />
          <div>
            <Title data-cy="agency-title">{name}</Title>
            <OffersCount data-cy="agency-count" title={offersCount}>
              {offersCount}
            </OffersCount>
          </div>
        </Row>

        <Description tag="caption_1_new" data-cy="agency-desc">
          {description}
        </Description>

        {!isClient ? (
          <PhonePlaceholder
            width="55px"
            height="55px"
            borderRadius="50%"
            margin="0"
          />
        ) : (
          isSmall && (
            <PhoneButtonContainer>
              <BottomPhoneButton
                isGallery={false}
                isIconButton
                phones={phonesShown}
                clickHandler={onClickHandler}
                contactInfoModalTitle={name}
                {...{ setIsVisible, isVisible }}
                stat="agency-call-click"
              />
            </PhoneButtonContainer>
          )
        )}
      </StyledAgencyCard>
    );
  }
);

AgencyCard.displayName = 'AgencyCard';
